import { AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { MyrAPIService } from '../../../api/myr/myrAPI';
import { IReduxRootState } from '../../../app/reduxRootState.interface';
import {
    ErrorResolution,
    ErrorResolutionTypes,
} from '../../../app/common/errorResolution/errorResolution';
import { VerifyEmailActionTypes } from './verifyEmailActionTypes';
import { MyrAPIEndpoints } from '../../../api/myr/myrAPIEndpoints.constant';
import { IVerifyEmailOtpPayload, ISendEmailOtpPayload } from './verifyEmailRequest';
import { NotificationsActions } from '../../../app/common/notifications/notificationsActions';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import {
    OtpStatesKey,
    StateCodeItem,
    processStateCodesStr,
    setStateCodesToLocalStorage,
} from './verifyEmailUtils';
import AccountSecurityActions from '../accountSecurityActions';
import { loginActions } from '../../../login/login.actions';
import { ErrorResolutionActions } from '../../../app/common/errorResolution/errorResolutionActions';
import { RevokeSessionService } from '../revokeSession/revokeSession.services';
import { RevokeSessionActionTypes } from '../revokeSession/revokeSessionActionTypes';
import ErrorRedirectService from '../../../routes/error/errorRedirect.service';
import { NOTIFICATIONS } from '../../../app/common/constants/styles';
import { NotificationType } from '../../../app/common/notifications/notificationType.enum';
import { addRatEvent } from '../../../utils/ratClickEvents';

export type SendEmailOtpResponse = {
    email: string;
    state: string;
    expiration: number;
};

export function validatePassword(
    payload: ISendEmailOtpPayload,
): ThunkAction<void, IReduxRootState, {}, AnyAction> {
    return async (dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>) => {
        try {
            dispatch({
                type: VerifyEmailActionTypes.SEND_OTP_REQUEST,
            });
            const endpoint = MyrAPIEndpoints.OTP_SEND + '?update_email=true';
            //validate pwd through otp/send
            const validateResult = (await MyrAPIService.sendRequest(
                endpoint,
                'POST',
                payload,
            )) as SendEmailOtpResponse;

            if (!!validateResult) {
                dispatch({ type: VerifyEmailActionTypes.VALIDATE_PASSWORD_SUCCESS });
                addRatEvent('email_verification', 'update_email');
            }
            const { state: value, expiration } = validateResult;

            const validOtpStates = processStateCodesStr({
                value,
                expiration: expiration + new Date().getTime(),
            });
            setStateCodesToLocalStorage(validOtpStates);

            dispatch({
                type: VerifyEmailActionTypes.SEND_OTP_SUCCESS,
            });
            dispatch({
                type: VerifyEmailActionTypes.TOO_MANY_SEND_OTP_REQUESTS,
                payload: false,
            });
            dispatch({
                type: VerifyEmailActionTypes.SET_PASSWORD_VALID_FALSE,
            });
        } catch (error) {
            dispatch({
                type: VerifyEmailActionTypes.CLEAR_OTP_TIMES,
            });
            const errorResolution = error as ErrorResolution;
            if (errorResolution.type === ErrorResolutionTypes.TOO_MANY_REQUESTS) {
                dispatch({ type: VerifyEmailActionTypes.VALIDATE_PASSWORD_SUCCESS });
                dispatch({
                    type: VerifyEmailActionTypes.TOO_MANY_SEND_OTP_REQUESTS,
                    payload: true,
                });
            } else {
                dispatch({ type: VerifyEmailActionTypes.VALIDATE_PASSWORD_FAILED });
                dispatch(
                    ErrorResolutionActions.processErrorResolution(
                        errorResolution,
                        'notifications.account.password.update.failure',
                    ),
                );
                addRatEvent('email_verification', 'invalid_password');
            }
            dispatch({
                type: VerifyEmailActionTypes.SET_PASSWORD_VALID_FALSE,
            });
        }
    };
}

export function sendExistingEmailOtp(
    payload: ISendEmailOtpPayload,
): ThunkAction<void, IReduxRootState, {}, AnyAction> {
    return async (dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>) => {
        try {
            dispatch({
                type: VerifyEmailActionTypes.SEND_OTP_REQUEST,
            });

            const response = (await MyrAPIService.sendRequest(
                MyrAPIEndpoints.OTP_SEND,
                'POST',
                payload,
            )) as SendEmailOtpResponse;
            const { state: value, expiration } = response;

            const validOtpStates = processStateCodesStr({
                value,
                expiration: expiration + new Date().getTime(),
            });
            setStateCodesToLocalStorage(validOtpStates);

            dispatch({
                type: VerifyEmailActionTypes.SEND_OTP_SUCCESS,
            });
            dispatch({
                type: VerifyEmailActionTypes.TOO_MANY_SEND_OTP_REQUESTS,
                payload: false,
            });
        } catch (error) {
            const errorResolution = error as ErrorResolution;
            if (errorResolution.type === ErrorResolutionTypes.TOO_MANY_REQUESTS) {
                dispatch({
                    type: VerifyEmailActionTypes.TOO_MANY_SEND_OTP_REQUESTS,
                    payload: true,
                });
            } else {
                dispatch({ type: VerifyEmailActionTypes.SEND_OTP_FAILURE });
            }
        }
    };
}

export function verifyEmailOtp(
    payload: IVerifyEmailOtpPayload,
): ThunkAction<void, IReduxRootState, {}, AnyAction> {
    return async (
        dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
        getState: () => IReduxRootState,
    ) => {
        try {
            const stateCodesStr = localStorage.getItem(OtpStatesKey);
            if (stateCodesStr) {
                const stateCodes = JSON.parse(stateCodesStr) as StateCodeItem[];
                const signOutFromAllDevices =
                    getState().accountSecurity.signOutFromAllDevices ?? false;
                if (payload.currentEmail) {
                    await MyrAPIService.sendRequest(MyrAPIEndpoints.EMAIL_VERIFY, 'PUT', {
                        ...payload,
                        state: stateCodes.map((item) => item.value),
                    });
                    addRatEvent('email_verification', 'verify_existing_email_success');
                } else {
                    await MyrAPIService.sendRequest(MyrAPIEndpoints.EMAIL_UPDATE, 'POST', {
                        ...payload,
                        state: stateCodes.map((item) => item.value),
                    });
                    addRatEvent('email_verification', 'verify_new_email_success');
                }
                localStorage.removeItem(OtpStatesKey);
                dispatch(AccountSecurityActions.closeAccountSecurityOverlay());
                dispatch(AccountSecurityActions.clearPassword());
                // MEMQA-12811: Handled duplicate notifications with session revocation due to session expired while fetching the member details
                if (signOutFromAllDevices) {
                    dispatch(handleSessionRevocation());
                } else {
                    // MEMQA-12815: Added the success notification upon email update
                    dispatch(
                        NotificationsActions.showSuccess(
                            TranslationHelpers.getFormattedMessage(
                                'notifications.account.email.update.success',
                            ),
                        ),
                    );
                    dispatch(loginActions.getMember());
                }
            } else {
                dispatch({
                    type: VerifyEmailActionTypes.VERIFY_EMAIL_FAILURE,
                });
            }
        } catch (error) {
            const errorResolution = error as ErrorResolution;
            if (errorResolution.type === ErrorResolutionTypes.TOO_MANY_REQUESTS) {
                dispatch({
                    type: VerifyEmailActionTypes.TOO_MANY_VERIFY_OTP_REQUESTS,
                    payload: true,
                });
            } else if (errorResolution.type === ErrorResolutionTypes.EMAIL_CONFLICT) {
                dispatch(
                    NotificationsActions.showError(
                        TranslationHelpers.getFormattedMessage(
                            'notifications.account.email.update.failure.rid-1004',
                        ),
                    ),
                );
                dispatch(AccountSecurityActions.closeAccountSecurityOverlay());
                return;
            }
            errorResolution.type !== ErrorResolutionTypes.TOO_MANY_REQUESTS &&
                dispatch({
                    type: VerifyEmailActionTypes.TOO_MANY_VERIFY_OTP_REQUESTS,
                    payload: false,
                });
            dispatch({
                type: VerifyEmailActionTypes.VERIFY_EMAIL_FAILURE,
            });
            addRatEvent('email_verification', 'verify_otp_fail');
        }
    };
}

export function setVerifyingEmail(email: string) {
    return {
        type: VerifyEmailActionTypes.SET_VERIFYING_EAMIL,
        payload: email,
    };
}

function handleSessionRevocation(): ThunkAction<void, IReduxRootState, {}, AnyAction> {
    return async (
        dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
        getState: () => IReduxRootState,
    ) => {
        try {
            dispatch({ type: RevokeSessionActionTypes.SEND_SESSION_REVOKE_REQUEST });
            const sessionRevokeRespone = await RevokeSessionService.requestSessionRevoke();
            if (!!sessionRevokeRespone) {
                const tokenExpirationUrl = getState().app.clientConfig.tokenExpirationUrl;
                successWithSessionRevoke(dispatch, tokenExpirationUrl);
            }
        } catch (error) {
            const errorResolution = error as ErrorResolution;
            dispatch({
                type: RevokeSessionActionTypes.SEND_SESSION_REVOKE_FAILURE,
                payload: { errorResolution: errorResolution },
            });
        }

        function successWithSessionRevoke(
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            tokenExpirationUrl: string,
        ) {
            addRatEvent('session_deactivation', 'revoke_user_sessions_success');
            dispatch({
                type: RevokeSessionActionTypes.SEND_SESSION_REVOKE_SUCCESS,
            });
            dispatch(
                NotificationsActions.showRedirectSuccess(
                    'notification.revoke.session.success.email.update.success',
                ),
            );
            // redirect the user to login page after session revocation
            setInterval(() => {
                ErrorRedirectService.takeToLogout(tokenExpirationUrl.toString());
            }, NOTIFICATIONS[NotificationType.Success_Redirecting].expiry);
        }
    };
}
